<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { getCookiePreferences } from '$lib/helper';
    import { mediaCookiesAccepted } from '$lib/Stores/stores';
    import { type VideoContent, VideoType } from '$lib/types/Components/Video/VideoContent';
    import { cn } from '$lib/utils/cn';

    import get from 'lodash/get';
    import { onMount } from 'svelte';

    import VideoView from './Video.View.svelte';
    import MediaOverlay from '../../ConsentOverlay/MediaOverlay/MediaOverlay.svelte';

    import type { Language } from '$lib/types/Language';
    import type { TransitionEffect } from '$lib/types/Theme';

    export let id = '';
    export let content: VideoContent;
    export let campaignId = '';
    export let campaignVersionId = '';
    export let cookiesEnabled = false;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let language: Language = 'en';
    export let layout = DEFAULT_LAYOUT;

    let isMobile = false;
    let isInFullscreen = false;

    const setIsMobile = () => {
        // Skip mobile state updates when in fullscreen to avoid interference
        if (isInFullscreen) return;
        isMobile = window.innerWidth <= 768;
    };

    const videoType = get(content, 'videoType', VideoType.ThirdParty);
    const isCustomVideo = videoType === VideoType.Custom;
    let url = isCustomVideo ? content.customVideoUrl : content.video;
    let thumbnail = isCustomVideo ? get(content, 'thumbnail') ?? '' : '';
    const cookies = getCookiePreferences(campaignId, campaignVersionId);
    const bgColor = get(content, 'box.backgroundColor');
    const borderRadius = getBorderRadius('video');

    $: customControls = isCustomVideo && !isMobile;
    $: acceptedMediaOverlay = cookies.Media || $mediaCookiesAccepted;

    onMount(() => {
        setIsMobile();
        window.addEventListener('resize', setIsMobile);

        // Track fullscreen state to prevent mobile updates during fullscreen
        const handleFullscreenChange = () => {
            isInFullscreen = !!document.fullscreenElement;
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            window.removeEventListener('resize', setIsMobile);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    });
</script>

{#if !isCustomVideo && cookiesEnabled && !acceptedMediaOverlay}
    <MediaOverlay {id} {campaignId} {campaignVersionId} {language} {bgColor} {layout} />
{:else}
    <VideoView
        {id}
        {url}
        autoplay={content.autoplay}
        box={content.box}
        borderRadiusClass={cn(borderRadius, {
            'max-sm:fun-rounded-none': !layout.isInFramedColumn,
        })}
        showSubtitles={content.showSubtitles}
        aspectRatio={content.aspectRatio}
        {layout}
        {transitionDelay}
        {transitionEffect}
        {thumbnail}
        {customControls}
        {isCustomVideo}
        {isMobile}
        {language}
    />
{/if}
